/**
 * Design §19.4 chrome for S5: collapsed platform fence summary from the bound
 * published version (overrides stay in the JSON panel).
 *
 * Execution mode is not shown — claim quality is defaultDecisionMode; runs stay
 * propose+approve.
 */

import React, { useMemo } from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';

interface BindingGovernanceSummaryProps {
  /** @deprecated Kept for call-site stability; not displayed. */
  executionMode?: string;
  versionAutonomyFences?: Record<string, unknown>;
}

function formatFenceValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

export const BindingGovernanceSummary: React.FC<BindingGovernanceSummaryProps> = ({
  versionAutonomyFences,
}) => {
  const { t } = useLanguage();

  const fenceEntries = useMemo(() => {
    const fences = versionAutonomyFences ?? {};
    return Object.entries(fences).slice(0, 8);
  }, [versionAutonomyFences]);

  return (
    <section className="rounded border border-[var(--color-border)] bg-[var(--color-secondary-bg)] px-4 py-3">
      <p className="mb-1 text-base font-medium text-[var(--color-surface-text)]">
        {t('aiStudio.bindings.platformFences')}
      </p>
      <p className="mb-2 text-base text-[var(--color-surface-text-secondary)]">
        {t('aiStudio.bindings.platformFencesHint')}
      </p>
      {fenceEntries.length === 0 ? (
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {t('aiStudio.bindings.platformFencesEmpty')}
        </p>
      ) : (
        <dl className="grid gap-1 sm:grid-cols-2">
          {fenceEntries.map(([key, value]) => (
            <div key={key} className="flex gap-2 text-base text-[var(--color-surface-text)]">
              <dt className="font-medium">{key}</dt>
              <dd className="truncate text-[var(--color-surface-text-secondary)]">
                {formatFenceValue(value)}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
};
